import React from "react";
import { ModeRendererProps } from "./types";

// VASARELY × DALI: Surreal systems — dreamlike recursion
// Melting grids, fluid geometry, optical illusions
export function VasarelyDaliMode({ brand }: ModeRendererProps) {
  return (
    <g>
      {/* Dreamlike background */}
      <defs>
        <linearGradient id="dali-dream" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#1a1a2e" stopOpacity="1" />
          <stop offset="50%" stopColor={brand.violet} stopOpacity="0.3" />
          <stop offset="100%" stopColor={brand.deep} stopOpacity="0.9" />
        </linearGradient>
      </defs>

      <rect x="0" y="0" width="794" height="1123" fill="url(#dali-dream)" />

      <g transform="translate(397 540)">
        {/* Melting grid */}
        {Array.from({ length: 14 }).map((_, i) =>
          Array.from({ length: 18 }).map((_, j) => {
            const x = (i - 6.5) * 44;
            const y = (j - 8.5) * 42;
            const melt = Math.max(0, (j - 5) * 8 + Math.sin(i * 0.5) * 15);
            
            return (
              <rect
                key={`${i}-${j}`}
                x={x - 16}
                y={y - 16 + melt}
                width="32"
                height={32 + melt * 0.5}
                fill={j > 8 ? brand.amber : brand.aqua}
                opacity={0.2 + (j > 8 ? 0.1 : 0)}
              />
            );
          })
        )}

        {/* Fluid geometry waves */}
        {Array.from({ length: 12 }).map((_, i) => {
          const y = -240 + i * 40;
          const points = Array.from({ length: 40 }).map((_, j) => {
            const x = -300 + j * 15;
            const wave = Math.sin(j * 0.3 + i * 0.4) * (30 + i * 3);
            const drip = Math.max(0, (i - 6) * 8);
            return `${x},${y + wave + drip}`;
          }).join(" ");
          
          return (
            <polyline
              key={i}
              points={points}
              fill="none"
              stroke={i % 3 === 0 ? brand.violet : i % 3 === 1 ? brand.aqua : brand.amber}
              strokeWidth="3"
              opacity={0.3}
            />
          );
        })}

        {/* Impossible optical circles */}
        {Array.from({ length: 20 }).map((_, i) => {
          const angle = i * 0.6;
          const r = 40 + i * 12;
          const x = Math.cos(angle) * 25;
          const y = Math.sin(angle) * 25;
          const distortion = Math.sin(i * 0.4) * 20;
          
          return (
            <ellipse
              key={i}
              cx={x + distortion}
              cy={y}
              rx={r}
              ry={r * (1 + Math.sin(i * 0.5) * 0.3)}
              fill="none"
              stroke={i % 2 === 0 ? brand.violet : brand.aqua}
              strokeWidth="2"
              opacity={0.35 - i * 0.012}
            />
          );
        })}

        {/* Surreal recursive shapes */}
        {[
          { cx: -140, cy: -100, size: 70 },
          { cx: 150, cy: -90, size: 65 },
          { cx: -110, cy: 140, size: 75 },
          { cx: 130, cy: 150, size: 68 },
        ].map((shape, i) => (
          <g key={i}>
            {/* Melting square into circle */}
            <path
              d={`
                M ${shape.cx - shape.size / 2} ${shape.cy - shape.size / 2}
                Q ${shape.cx} ${shape.cy - shape.size} ${shape.cx + shape.size / 2} ${shape.cy - shape.size / 2}
                Q ${shape.cx + shape.size} ${shape.cy} ${shape.cx + shape.size / 2} ${shape.cy + shape.size / 2}
                L ${shape.cx} ${shape.cy + shape.size * 0.8}
                L ${shape.cx - shape.size / 2} ${shape.cy + shape.size / 2}
                Q ${shape.cx - shape.size} ${shape.cy} ${shape.cx - shape.size / 2} ${shape.cy - shape.size / 2}
                Z
              `}
              fill={brand.violet}
              opacity="0.25"
            />
            {/* Inner recursive form */}
            <circle
              cx={shape.cx}
              cy={shape.cy}
              r={shape.size * 0.4}
              fill={brand.aqua}
              opacity="0.2"
            />
          </g>
        ))}

        {/* Dream recursion spiral */}
        {Array.from({ length: 40 }).map((_, i) => {
          const angle = i * 0.7;
          const r = 10 + i * 7;
          const x = Math.cos(angle) * r;
          const y = Math.sin(angle) * r;
          const drip = Math.max(0, i - 20) * 2;
          
          return (
            <ellipse
              key={i}
              cx={x}
              cy={y + drip}
              rx="6"
              ry={8 + drip * 0.3}
              fill={i % 3 === 0 ? brand.violet : i % 3 === 1 ? brand.aqua : brand.amber}
              opacity={0.5 - i * 0.01}
            />
          );
        })}

        {/* Floating time nodes (melting clocks metaphor) */}
        {[
          { x: -180, y: -140, angle: -15 },
          { x: 190, y: -120, angle: 20 },
          { x: -160, y: 160, angle: -25 },
          { x: 170, y: 180, angle: 18 },
        ].map((clock, i) => (
          <g key={i}>
            <ellipse
              cx={clock.x}
              cy={clock.y}
              rx="50"
              ry="30"
              fill={brand.amber}
              opacity="0.3"
              transform={`rotate(${clock.angle} ${clock.x} ${clock.y})`}
            />
            <line
              x1={clock.x}
              y1={clock.y}
              x2={clock.x + 20}
              y2={clock.y}
              stroke={brand.violet}
              strokeWidth="3"
              opacity="0.5"
            />
            <line
              x1={clock.x}
              y1={clock.y}
              x2={clock.x}
              y2={clock.y + 15}
              stroke={brand.aqua}
              strokeWidth="3"
              opacity="0.5"
            />
          </g>
        ))}

        {/* Impossible frame */}
        <rect x="-250" y="-220" width="500" height="440" fill="none" stroke={brand.violet} strokeWidth="3" opacity="0.3" />
      </g>

      {/* Surreal inscription */}
      <text x="397" y="1050" textAnchor="middle" fontFamily="serif" fontSize="11" fill={brand.violet} opacity="0.6" fontStyle="italic">
        sueño de la razón digital
      </text>
    </g>
  );
}
